import React from 'react';
import { useSelector } from 'react-redux';
import { CardHeaderRow, Pill, EmptyState } from '../../../ui';
import { OrgNiveau, OrgGrille, OrgLien, MonoCell } from '../../../security/security.styled';
import { CartePersonne } from '../personne';

/* ═══════════════════════════════════════════════════════════════════
   Organisation : trois niveaux, et non plus un seul.

   L'ancienne version montrait le compte consulté puis ses rattachés, avec une
   flèche entre les deux — le manager n'y figurait pas, alors que l'information
   est dans la fiche. Une organisation qui ne remonte pas est à moitié vide.

   Une grille plutôt qu'un arbre centré : le plus gros encadrement du parc
   compte 27 rattachés, un arbre dessiné déborderait de la colonne. 156 comptes
   encadrent au moins une personne.

   Chaque carte mène à la fiche de la personne — le point d'entrée renvoie
   désormais l'UPN des rattachés, sans lequel l'organigramme ne conduisait
   nulle part.
   ═══════════════════════════════════════════════════════════════════ */

function parNom(a, b) {
    return String(a?.displayName || '').localeCompare(String(b?.displayName || ''), 'fr', { sensitivity: 'base' });
}

export function UserOrganization() {
    const { currentOrganization, isActiveOrganization } = useSelector((state) => state.organization);
    const { currentUser } = useSelector((state) => state.user);

    // Le manager n'est connu que par son UPN : la collecte ne stocke que
    // l'adresse, pas une référence. Le nom affiché est donc l'adresse — c'est
    // ce que la base contient, et le lien fonctionne puisque l'UPN EST la clé.
    const managerUpn = currentUser?.manager || null;

    const compte = isActiveOrganization ? currentOrganization : null;
    const rattaches = [...(compte?.children || [])].sort(parNom);
    const desactives = rattaches.filter((r) => r.accountEnabled === false).length;

    if (!compte && !managerUpn) {
        return (
            <>
                <CardHeaderRow><h2>Organisation</h2></CardHeaderRow>
                <EmptyState>
                    <i className="bx bx-sitemap" />
                    <h3>Aucun rattachement connu</h3>
                    <p>Ce compte n'a ni manager renseigné ni collaborateur rattaché.</p>
                </EmptyState>
            </>
        );
    }

    return (
        <>
            <CardHeaderRow style={{ flexWrap: 'wrap' }}>
                <h2>Organisation</h2>
                {rattaches.length > 0 && (
                    <Pill $tone="neutral">
                        {rattaches.length} rattaché{rattaches.length > 1 ? 's' : ''}
                    </Pill>
                )}
                {/* Un rattaché désactivé continue d'apparaître dans l'équipe :
                    la collecte le retient tant qu'il porte ce manager. */}
                {desactives > 0 && (
                    <Pill $tone="danger">
                        {desactives} désactivé{desactives > 1 ? 's' : ''}
                    </Pill>
                )}
            </CardHeaderRow>

            {managerUpn && (
                <>
                    <OrgNiveau>Manager</OrgNiveau>
                    <div style={{ maxWidth: '340px' }}>
                        <CartePersonne
                            personne={{ displayName: managerUpn, userPrincipalName: managerUpn }}
                            sous="Responsable hiérarchique"
                        />
                    </div>
                    <OrgLien />
                </>
            )}

            <OrgNiveau>Ce compte</OrgNiveau>
            <div style={{ maxWidth: '340px' }}>
                {/* Sans UPN transmis : la carte reste inerte, on est déjà sur
                    cette fiche. */}
                <CartePersonne
                    personne={{
                        displayName: compte?.displayName || currentUser?.displayName,
                        profilePicture: compte?.profilePicture ?? currentUser?.profilePicture,
                    }}
                    sous={compte?.jobTitle || currentUser?.jobTitle || compte?.department || currentUser?.department}
                />
            </div>

            {rattaches.length > 0 && (
                <>
                    <OrgLien />
                    <OrgNiveau>
                        {rattaches.length} collaborateur{rattaches.length > 1 ? 's' : ''} rattaché{rattaches.length > 1 ? 's' : ''}
                    </OrgNiveau>
                    <OrgGrille>
                        {rattaches.map((personne, index) => (
                            <CartePersonne
                                key={personne.userPrincipalName || `${personne.displayName}-${index}`}
                                personne={personne}
                                sous={personne.jobTitle || personne.department}
                            />
                        ))}
                    </OrgGrille>
                </>
            )}

            {rattaches.length === 0 && (
                <EmptyState style={{ padding: '32px 20px' }}>
                    <i className="bx bx-user-voice" />
                    <h3>Aucun collaborateur rattaché</h3>
                    <p>
                        Aucun compte du parc ne déclare
                        {' '}<MonoCell>{compte?.userPrincipalName || currentUser?.userPrincipalName}</MonoCell>{' '}
                        comme manager. 156 comptes encadrent au moins une personne.
                    </p>
                </EmptyState>
            )}
        </>
    );
}
